import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  signOut,
} from "firebase/auth";
import { authentication } from "../../firebase";
import { logAuthEvent, hashIdentifier } from "../../utils/logAuthEvent";
import { initMixpanel } from "@/utils/analytics/mixpanel";
import { trackProductEvent } from "@/utils/analytics/track";
import "./DemoSignupScreen.css";
import { useAuth } from "@/features/auth/useAuth";

const DEMO_API_URL = "https://demo-signup-263487073985.us-central1.run.app";
const DEMO_API_KEY = process.env.REACT_APP_DEMO_API_KEY || "";

// Throwaway password used only to mint the Firebase email/password identity.
// The user never sees or uses it - they set their real password via the
// reset link we email them, which is what proves they own the address.
function generateThrowawayPassword() {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const random = btoa(String.fromCharCode(...bytes)).replace(
    /[^a-zA-Z0-9]/g,
    "",
  );
  return `Aa1!${random}`.slice(0, 28);
}

const ALL_STEPS = [
  {
    key: "fullName",
    label: "What's your full name?",
    required: true,
    type: "text",
    placeholder: "Type your name here...",
  },
  {
    key: "email",
    label: "What's your email address?",
    required: true,
    type: "email",
    placeholder: "you@school.edu",
  },
  {
    key: "password",
    label: "Create a password",
    required: true,
    type: "password",
    placeholder: "At least 6 characters",
  },
  {
    key: "organization",
    label: "What's your school or organization? (Optional)",
    required: false,
    type: "text",
    placeholder: "e.g., Lincoln Elementary School",
  },
  {
    key: "role",
    label: "I am a...",
    required: true,
    type: "select",
    options: [
      { value: "", label: "Select one..." },
      { value: "educator", label: "Educator" },
      { value: "teacher", label: "Teacher" },
      { value: "counselor", label: "Counselor" },
      { value: "principal", label: "Principal" },
      { value: "superintendent", label: "Superintendent" },
      { value: "admin", label: "Administrator" },
      { value: "wellness-coach", label: "Wellness Coach" },
      { value: "therapist", label: "Therapist" },
      { value: "student", label: "Student" },
      { value: "parent", label: "Parent" },
      { value: "other", label: "Other" },
    ],
  },
  {
    key: "howDidYouHear",
    label: "How did you hear about us? (Optional)",
    required: false,
    type: "select",
    options: [
      { value: "", label: "Select one..." },
      { value: "google", label: "Google Search" },
      { value: "social-media", label: "Social Media" },
      { value: "referral", label: "Referral" },
      { value: "conference", label: "Conference/Event" },
      { value: "other", label: "Other" },
    ],
  },
  {
    key: "prefillData",
    label: "Would you like to start with sample wellness data?",
    required: true,
    type: "select",
    options: [
      { value: "", label: "Select one..." },
      { value: "yes", label: "Yes, show me sample data (recommended)" },
      { value: "no", label: "No, I'll start fresh" },
    ],
  },
];

const DemoSignupScreen = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const formStartRef = useRef(performance.now());
  const stepStartRef = useRef(performance.now());
  const viewTrackedRef = useRef(false);
  const abandonTrackedRef = useRef(false);
  const authStartRef = useRef(null);
  const successTrackedRef = useRef(false);
  const rageClickRef = useRef({});
  const errorCountsRef = useRef({});
  const demoContextRef = useRef({});
  const formDataRef = useRef({});
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    organization: "",
    role: "educator",
    howDidYouHear: "",
    prefillData: "yes",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [setupEmailSent, setSetupEmailSent] = useState(false);
  const [passwordFlow, setPasswordFlow] = useState(false);
  // Sign-in methods Firebase reports for an address that already has an
  // identity. null means "we don't know" - the fallback then offers every
  // option, exactly as before.
  const [availableMethods, setAvailableMethods] = useState(null);

  const { firebaseLogin } = useAuth();

  // NY/SSAP DAST gate: the in-flow "Create a password + sign in immediately"
  // path (option B) is exposed ONLY to the security scanner's IPs. Everyone
  // else gets the standard email-verified path (option A). The decision is made
  // at the edge from the caller's real IP (functions/api/demo-flow-mode.js);
  // we fail closed to the standard flow. TEMPORARY - remove with that Function.
  const STEPS = passwordFlow
    ? ALL_STEPS
    : ALL_STEPS.filter((step) => step.key !== "password");

  // Ask the edge whether this visitor's IP is on the scanner allowlist.
  // Any error (network, non-200, bad body) leaves passwordFlow=false.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/demo-flow-mode", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setPasswordFlow(Boolean(data?.passwordFlow));
      } catch {
        // stay on the standard flow
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const trackDemoEvent = (eventName, props = {}) => {
    trackProductEvent(
      eventName,
      {
        page: window.location.pathname,
        ...demoContextRef.current,
        ...props,
      },
      null,
      { allowAnonymous: true },
    );
  };

  const getReferrerClass = () => {
    const referrer = document.referrer || "";
    if (!referrer) return "direct";
    try {
      const refUrl = new URL(referrer);
      const host = refUrl.hostname || "";
      const currentHost = window.location.hostname;
      if (host === currentHost) return "internal";
      if (/(google|bing|yahoo|duckduckgo)\./i.test(host)) return "search";
      if (
        /(facebook|instagram|linkedin|twitter|x\.com|t\.co|tiktok)\./i.test(
          host,
        )
      ) {
        return "social";
      }
      return "external";
    } catch {
      return "external";
    }
  };

  const getUtmParams = () => {
    const params = new URLSearchParams(window.location.search || "");
    return {
      utm_source: params.get("utm_source") || undefined,
      utm_medium: params.get("utm_medium") || undefined,
      utm_campaign: params.get("utm_campaign") || undefined,
      utm_term: params.get("utm_term") || undefined,
      utm_content: params.get("utm_content") || undefined,
    };
  };

  const trackRageClick = (buttonId) => {
    const now = performance.now();
    const windowMs = 1000;
    const maxWindow = 3;
    const clicks = rageClickRef.current[buttonId] || [];
    const recent = clicks.filter((t) => now - t <= windowMs);
    recent.push(now);
    rageClickRef.current[buttonId] = recent;
    if (recent.length >= maxWindow) {
      const meta = getStepMeta(currentStep);
      trackDemoEvent("demo_signup_rage_click", {
        button_id: buttonId,
        click_count: recent.length,
        window_ms: windowMs,
        step_key: meta.step_key,
        step_index: meta.step_index,
      });
      rageClickRef.current[buttonId] = [];
    }
  };

  const trackRepeatedError = (errorType, stepIndex) => {
    const meta = getStepMeta(stepIndex);
    const key = `${meta.step_key}:${errorType}`;
    const next = (errorCountsRef.current[key] || 0) + 1;
    errorCountsRef.current[key] = next;
    if (next === 3) {
      trackDemoEvent("demo_signup_repeated_error", {
        step_key: meta.step_key,
        step_index: meta.step_index,
        error_type: errorType,
        count: next,
      });
    }
  };

  const getStepMeta = (stepIndex) => {
    const step = STEPS[stepIndex];
    return {
      step,
      step_key: step?.key || "unknown",
      step_index: stepIndex,
      total_steps: STEPS.length,
      required: Boolean(step?.required),
      input_type: step?.type || "unknown",
    };
  };

  // Keep ref in sync so setTimeout closures always read the latest formData
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Auto-focus input when step changes
  useEffect(() => {
    if (inputRef.current && currentStep < STEPS.length) {
      inputRef.current.focus();
    }
  }, [currentStep]);

  useEffect(() => {
    initMixpanel({
      token: process.env.REACT_APP_MIXPANEL_TOKEN,
      enabled: true,
    });

    if (!viewTrackedRef.current) {
      demoContextRef.current = {
        ...getUtmParams(),
        referrer_class: getReferrerClass(),
      };
      trackDemoEvent("demo_signup_entry", {
        ...demoContextRef.current,
      });
      const meta = getStepMeta(currentStep);
      trackDemoEvent("demo_signup_view", {
        step_key: meta.step_key,
        step_index: meta.step_index,
        total_steps: meta.total_steps,
      });
      trackDemoEvent("demo_signup_step_view", meta);
      viewTrackedRef.current = true;
    }
    formStartRef.current = performance.now();
    stepStartRef.current = performance.now();
  }, []);

  useEffect(() => {
    const meta = getStepMeta(currentStep);
    trackDemoEvent("demo_signup_step_view", meta);
    stepStartRef.current = performance.now();
  }, [currentStep]);

  useEffect(() => {
    const handleAbandon = () => {
      if (abandonTrackedRef.current) return;
      const meta = getStepMeta(currentStep);
      const totalDurationMs = Math.max(
        0,
        Math.round(performance.now() - formStartRef.current),
      );
      const stepDurationMs = Math.max(
        0,
        Math.round(performance.now() - stepStartRef.current),
      );
      trackDemoEvent("demo_signup_abandon", {
        step_key: meta.step_key,
        step_index: meta.step_index,
        total_duration_ms: totalDurationMs,
        step_duration_ms: stepDurationMs,
        had_errors: Boolean(Object.keys(errors || {}).length),
        success: Boolean(success),
      });
      abandonTrackedRef.current = true;
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        handleAbandon();
      }
    };

    window.addEventListener("beforeunload", handleAbandon);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("beforeunload", handleAbandon);
      document.removeEventListener("visibilitychange", handleVisibility);
      handleAbandon();
    };
  }, [currentStep, errors, success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Auto-advance for select fields when a value is selected (except skip option)
    const step = STEPS[currentStep];
    if (step.type === "select" && value && value !== "") {
      // Small delay to ensure state is updated
      setTimeout(() => {
        handleNext();
      }, 100);
    }
  };

  const validateCurrentStep = () => {
    const step = STEPS[currentStep];
    if (!step.required) return true;

    const value = formData[step.key];
    if (step.key === "email") {
      return value && /\S+@\S+\.\S+/.test(value);
    }
    if (step.key === "password") {
      return value && value.length >= 6;
    }
    return value && value.trim().length > 0;
  };

  const handleNext = (e) => {
    e?.preventDefault();
    trackRageClick("demo_submit");

    const step = STEPS[currentStep];
    const value = formData[step.key];

    // Validate required fields
    if (step.required) {
      if (step.key === "email") {
        if (!value || !/\S+@\S+\.\S+/.test(value)) {
          const meta = getStepMeta(currentStep);
          trackDemoEvent("demo_signup_validation_error", {
            step_key: meta.step_key,
            step_index: meta.step_index,
            error_type: "invalid_email",
          });
          trackRepeatedError("invalid_email", currentStep);
          setErrors({ [step.key]: "Please enter a valid email address" });
          return;
        }
      } else if (step.key === "password") {
        if (!value || value.length < 6) {
          const meta = getStepMeta(currentStep);
          trackDemoEvent("demo_signup_validation_error", {
            step_key: meta.step_key,
            step_index: meta.step_index,
            error_type: "weak_password",
          });
          trackRepeatedError("weak_password", currentStep);
          setErrors({ [step.key]: "Password must be at least 6 characters" });
          return;
        }
      } else if (!value || !value.trim()) {
        const meta = getStepMeta(currentStep);
        trackDemoEvent("demo_signup_validation_error", {
          step_key: meta.step_key,
          step_index: meta.step_index,
          error_type: "required",
        });
        trackRepeatedError("required", currentStep);
        setErrors({ [step.key]: "This field is required" });
        return;
      }
    }

    // Clear errors
    setErrors({});

    const meta = getStepMeta(currentStep);
    const durationMs = Math.max(
      0,
      Math.round(performance.now() - stepStartRef.current),
    );
    const selectedValue =
      step.type === "select" ? (value || "").toString() : undefined;
    const durationBucket =
      durationMs <= 5000
        ? "fast"
        : durationMs <= 15000
          ? "ok"
          : durationMs <= 30000
            ? "slow"
            : "very_slow";
    trackDemoEvent("demo_signup_step_complete", {
      step_key: meta.step_key,
      step_index: meta.step_index,
      duration_ms: durationMs,
      duration_bucket: durationBucket,
      value_length: typeof value === "string" ? value.length : 0,
      selected_value: selectedValue,
      required: meta.required,
      input_type: meta.input_type,
    });

    // Move to next step or submit
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit(e);
    }
  };

  const handleSkip = () => {
    trackRageClick("demo_skip");
    const meta = getStepMeta(currentStep);
    const durationMs = Math.max(
      0,
      Math.round(performance.now() - stepStartRef.current),
    );
    trackDemoEvent("demo_signup_step_skip", {
      step_key: meta.step_key,
      step_index: meta.step_index,
      duration_ms: durationMs,
    });
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      trackRageClick("demo_back");
      const durationMs = Math.max(
        0,
        Math.round(performance.now() - stepStartRef.current),
      );
      trackDemoEvent("demo_signup_step_back", {
        from_step: currentStep,
        to_step: currentStep - 1,
        duration_ms: durationMs,
      });
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleNext(e);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      const completedAt = sessionStorage.getItem("__demoSignupCompletedAt");
      const delayMs = completedAt
        ? Math.max(0, Date.now() - parseInt(completedAt))
        : undefined;
      trackDemoEvent("demo_signup_auth_start", {
        method: "google",
        delay_ms: delayMs,
      });
      authStartRef.current = performance.now();
      window.__demoAuthPending = { method: "google", startedAt: Date.now() };
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(authentication, provider);
      const idToken = await result.user.getIdToken();
      await firebaseLogin(idToken);
      const durationMs = authStartRef.current
        ? Math.max(0, Math.round(performance.now() - authStartRef.current))
        : undefined;
      trackDemoEvent("demo_signup_auth_success", {
        method: "google",
        duration_ms: durationMs,
      });
      window.__demoAuthSuccessTracked = true;
      // Use sessionStorage for persistence across navigation/reload
      sessionStorage.setItem("__demoSignupSignedInAt", Date.now().toString());
      sessionStorage.setItem("__demoSignupFirstPagePending", "true");
      sessionStorage.setItem("__demoSignupSource", "demo_signup");
      logAuthEvent({
        message: "Demo signup: Google sign-in successful",
        level: "info",
        capture: true,
        data: { userId: result.user.uid },
      });
      // Navigate to home page after successful sign-in
      navigate("/");
    } catch (error) {
      trackDemoEvent("demo_signup_auth_failure", { method: "google" });
      logAuthEvent({
        message: "Demo signup: Google sign-in failed",
        error,
        level: "error",
        capture: true,
      });
      setErrors({ submit: "Google sign-in failed. Please try again." });
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      setIsSigningIn(true);
      window.__demoAuthPending = {
        method: "microsoft",
        startedAt: Date.now(),
      };
      const completedAt = sessionStorage.getItem("__demoSignupCompletedAt");
      const delayMs = completedAt
        ? Math.max(0, Date.now() - parseInt(completedAt))
        : undefined;
      trackDemoEvent("demo_signup_auth_start", {
        method: "microsoft",
        delay_ms: delayMs,
      });
      authStartRef.current = performance.now();
      const provider = new OAuthProvider("microsoft.com");
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(authentication, provider);
      const idToken = await result.user.getIdToken();
      await firebaseLogin(idToken);
      const durationMs = authStartRef.current
        ? Math.max(0, Math.round(performance.now() - authStartRef.current))
        : undefined;
      trackDemoEvent("demo_signup_auth_success", {
        method: "microsoft",
        duration_ms: durationMs,
      });
      window.__demoAuthSuccessTracked = true;
      // Use sessionStorage for persistence across navigation/reload
      sessionStorage.setItem("__demoSignupSignedInAt", Date.now().toString());
      sessionStorage.setItem("__demoSignupFirstPagePending", "true");
      sessionStorage.setItem("__demoSignupSource", "demo_signup");
      logAuthEvent({
        message: "Demo signup: Microsoft sign-in successful",
        level: "info",
        capture: true,
        data: { userId: result.user.uid },
      });
      // Navigate to home page after successful sign-in
      navigate("/");
    } catch (error) {
      trackDemoEvent("demo_signup_auth_failure", { method: "microsoft" });
      logAuthEvent({
        message: "Demo signup: Microsoft sign-in failed",
        error,
        level: "error",
        capture: true,
      });
      setErrors({ submit: "Microsoft sign-in failed. Please try again." });
    } finally {
      setIsSigningIn(false);
    }
  };

  // Option A: let any email finish setup by verifying ownership via an emailed
  // password-reset link. We mint a Firebase email/password identity with a
  // throwaway password, then immediately email a reset link so the user sets
  // their own password. They can't sign in until they complete that link,
  // which is what proves the address is real.
  const handleEmailSetup = async () => {
    try {
      setIsSigningIn(true);
      const email = formDataRef.current.email.toLowerCase().trim();
      trackDemoEvent("demo_signup_auth_start", { method: "email" });

      try {
        await createUserWithEmailAndPassword(
          authentication,
          email,
          generateThrowawayPassword(),
        );
      } catch (error) {
        // Already provisioned (e.g. user came back) - fall through and just
        // re-send the reset link. Any other error is surfaced below.
        if (error?.code !== "auth/email-already-in-use") {
          throw error;
        }
      }

      await sendPasswordResetEmail(authentication, email);
      // Don't leave the throwaway session active - force them through the link.
      await signOut(authentication).catch(() => {});

      setSetupEmailSent(true);
      trackDemoEvent("demo_signup_auth_success", { method: "email" });
      logAuthEvent({
        message: "Demo signup: password setup email sent",
        level: "info",
        capture: true,
        data: { emailHash: hashIdentifier(email) },
      });
    } catch (error) {
      trackDemoEvent("demo_signup_auth_failure", { method: "email" });
      logAuthEvent({
        message: "Demo signup: password setup email failed",
        error,
        level: "error",
        capture: true,
      });
      setErrors({
        submit: "Couldn't send the setup email. Please try again.",
      });
    } finally {
      setIsSigningIn(false);
    }
  };

  // Option B (NY/SSAP): the user sets a password directly in the flow, so we
  // mint the Firebase identity and sign them in immediately - no email
  // round-trip. Used because the SSAP scanners can't receive verification mail.
  const finishWithPassword = async (email, password) => {
    try {
      let credential;
      try {
        credential = await createUserWithEmailAndPassword(
          authentication,
          email,
          password,
        );
      } catch (error) {
        // Returning user whose identity already exists - sign in with the
        // password they just entered.
        if (error?.code === "auth/email-already-in-use") {
          credential = await signInWithEmailAndPassword(
            authentication,
            email,
            password,
          );
        } else {
          throw error;
        }
      }

      const idToken = await credential.user.getIdToken();
      await firebaseLogin(idToken);

      sessionStorage.setItem("__demoSignupSignedInAt", Date.now().toString());
      sessionStorage.setItem("__demoSignupFirstPagePending", "true");
      sessionStorage.setItem("__demoSignupSource", "demo_signup");
      logAuthEvent({
        message: "Demo signup: email/password sign-in successful",
        level: "info",
        capture: true,
        data: { emailHash: hashIdentifier(email) },
      });
      navigate("/");
      return true;
    } catch (error) {
      logAuthEvent({
        message: "Demo signup: email/password sign-in failed",
        error,
        level: "error",
        capture: true,
        data: { emailHash: hashIdentifier(email) },
      });
      // The Mongo account exists; fall back to the success screen so they can
      // sign in manually with the password they just set.
      setErrors({
        submit:
          "Your account was created, but we couldn't sign you in automatically. Please sign in with your email and password.",
      });
      setSuccess(true);
      return false;
    }
  };

  // Option A: put the user straight into the app instead of ending the flow on
  // a sign-in prompt. By the time we get here the Mongo account already exists,
  // so signing out and forcing the emailed link was gating the *session* while
  // leaving the account itself unverified - it cost the user a second login and
  // bought little. We mint the Firebase identity with a throwaway password, sign
  // in with it, and send the reset link in the background so they can set a
  // password they actually know before the next visit.
  //
  // Returns false only when the address already has an identity (typically a
  // Google/Microsoft account), which is the one case we can't mint a session
  // for here - the caller falls back to the provider buttons.
  // Shared tail for every path that manages to establish a session.
  const completeSignIn = async (idToken, email, method) => {
    await firebaseLogin(idToken);
    sessionStorage.setItem("__demoSignupSignedInAt", Date.now().toString());
    sessionStorage.setItem("__demoSignupFirstPagePending", "true");
    sessionStorage.setItem("__demoSignupSource", "demo_signup");
    trackDemoEvent("demo_signup_auth_success", { method });
    logAuthEvent({
      message: `Demo signup: ${method} sign-in successful`,
      level: "info",
      capture: true,
      data: { emailHash: hashIdentifier(email) },
    });
    navigate("/");
  };

  const finishWithAutoSignIn = async (email) => {
    try {
      // The Firebase session outlives the app's own auth state - firebase.js
      // uses browserLocalPersistence, while AuthGate renders this screen
      // whenever the *app* is unauthenticated. So the browser can already hold
      // a valid identity for this address. Reuse it rather than making them
      // sign in again for a session they already have.
      const current = authentication.currentUser;
      if (current?.email && current.email.toLowerCase() === email) {
        await completeSignIn(
          await current.getIdToken(),
          email,
          "existing_session",
        );
        return true;
      }

      const credential = await createUserWithEmailAndPassword(
        authentication,
        email,
        generateThrowawayPassword(),
      );

      // Sent regardless of what happens next, so that even a failed sign-in
      // leaves them a way in - they can never know the throwaway password.
      sendPasswordResetEmail(authentication, email).catch(() => {});

      await completeSignIn(await credential.user.getIdToken(), email, "auto");
      return true;
    } catch (error) {
      // An existing identity isn't a failure - it just means the address is
      // owned by a provider account, so they finish with one provider click.
      const hasExistingIdentity = error?.code === "auth/email-already-in-use";
      if (hasExistingIdentity) {
        // Narrow the fallback to the provider that will actually work, so they
        // aren't guessing between three buttons where two are wrong. Fails open
        // to all of them: enumeration protection can return an empty list.
        try {
          const methods = await fetchSignInMethodsForEmail(
            authentication,
            email,
          );
          if (methods?.length) {
            setAvailableMethods(methods);
          }
        } catch {
          // Leave availableMethods null and offer every option.
        }
      } else {
        trackDemoEvent("demo_signup_auth_failure", { method: "auto" });
      }
      logAuthEvent({
        message: hasExistingIdentity
          ? "Demo signup: auto sign-in skipped, identity already exists"
          : "Demo signup: auto sign-in failed",
        error: hasExistingIdentity ? undefined : error,
        level: hasExistingIdentity ? "info" : "error",
        capture: true,
        data: { emailHash: hashIdentifier(email) },
      });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Final validation
    const newErrors = {};
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (passwordFlow && (!formData.password || formData.password.length < 6)) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (!formData.fullName?.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      const errorStepIndex = STEPS.findIndex((s) => newErrors[s.key]);
      if (errorStepIndex !== -1) {
        const meta = getStepMeta(errorStepIndex);
        trackDemoEvent("demo_signup_validation_error", {
          step_key: meta.step_key,
          step_index: meta.step_index,
          error_type: "required",
        });
        trackRepeatedError("required", errorStepIndex);
        setCurrentStep(errorStepIndex);
      }
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    const totalDurationMs = Math.max(
      0,
      Math.round(performance.now() - formStartRef.current),
    );
    trackDemoEvent("demo_signup_submit", {
      total_duration_ms: totalDurationMs,
    });

    try {
      const latestFormData = formDataRef.current;
      const email = latestFormData.email.toLowerCase().trim();
      const password = latestFormData.password;
      const fullName = latestFormData.fullName.trim();

      // Prepare the payload - extend with form data
      const payload = {
        email: email,
        fullName: fullName,
        organization: latestFormData.organization.trim() || undefined,
        role: latestFormData.role,
        howDidYouHear: latestFormData.howDidYouHear || undefined,
        skipPrefill: latestFormData.prefillData === "no",
      };

      // Call your demo signup API
      const response = await fetch(DEMO_API_URL, {
        method: "POST",
        headers: {
          "x-demo-api-key": DEMO_API_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage =
          responseData?.error ||
          responseData?.message ||
          `API error: ${response.status} ${response.statusText}`;
        const err = new Error(errorMessage);
        err.status = response.status;
        throw err;
      }

      // Log successful signup
      logAuthEvent({
        message: "Demo account created successfully via API",
        level: "info",
        capture: true,
        data: {
          data: { emailHash: hashIdentifier(email) },
          fullName: fullName,
          organization: formData.organization,
          role: formData.role,
        },
      });

      if (!successTrackedRef.current) {
        trackDemoEvent("demo_signup_success", {
          total_duration_ms: totalDurationMs,
        });
        const completedAt = Date.now();
        // Use sessionStorage for persistence across navigation
        sessionStorage.setItem(
          "__demoSignupCompletedAt",
          completedAt.toString(),
        );
        sessionStorage.setItem("__demoSignupFirstPagePending", "true");
        sessionStorage.setItem("__demoSignupSource", "demo_signup");
        successTrackedRef.current = true;
      }

      if (passwordFlow) {
        // Option B (NY/SSAP): sign in immediately with the password they set.
        await finishWithPassword(email, password);
      } else {
        // Option A (everyone else): sign them straight into the app. Only fall
        // back to the success screen's provider buttons when the address
        // already has a Firebase identity we can't mint a session for.
        const signedIn = await finishWithAutoSignIn(email);
        if (!signedIn) {
          setSuccess(true);
        }
      }
    } catch (error) {
      console.error("Account creation error:", error);
      trackDemoEvent("demo_signup_error", {
        error_type: error?.message ? "api_error" : "unknown",
        status_code: error?.status,
      });
      logAuthEvent({
        message: `Demo account creation failed: ${error.message}`,
        error,
        level: "error",
        capture: true,
        data: { emailHash: hashIdentifier(formData.email) },
      });

      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        setErrors({
          submit:
            "Request blocked by CORS. Please allow http://localhost:3000 and your production domain in the API CORS settings.",
        });
      } else if (
        error.status === 409 ||
        error.message.includes("already exists")
      ) {
        const emailStepIndex = STEPS.findIndex((step) => step.key === "email");
        if (emailStepIndex >= 0) {
          setCurrentStep(emailStepIndex);
        }
        setErrors({
          email: "This email already exists. Please sign in instead.",
        });
      } else if (
        error.status === 400 ||
        error.message.includes("Invalid email")
      ) {
        setErrors({ email: "Please enter a valid email address." });
      } else if (
        error.status === 401 ||
        error.message.includes("Unauthorized")
      ) {
        setErrors({ submit: "Unauthorized. Please check the demo API key." });
      } else {
        setErrors({
          submit:
            error.message || "Failed to create account. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Until Firebase tells us which methods the address actually has, every
  // option stays visible - narrowing is a refinement, never a gate.
  const showSignInMethod = (method) =>
    !availableMethods?.length || availableMethods.includes(method);

  if (success) {
    return (
      <div className="demo-signup-container">
        <div className="demo-signup-content">
          <div className="success-message">
            <img
              className="success-fireworks"
              src="/images/Fireworks.gif"
              alt="Fireworks"
            />
            <div className="success-badge">🎉</div>
            <h2>You're In! 🎉</h2>
            <p className="success-text">
              Your demo account is ready. Let’s unlock calm, focus, and
              connection—one moment at a time.
            </p>
            <p className="success-email">
              <strong>{formData.email}</strong>
            </p>
            {setupEmailSent ? (
              <p className="signin-instructions">
                We’ve emailed <strong>{formData.email}</strong> a link to set
                your password. Once you’ve set it, come back and{" "}
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/");
                  }}
                >
                  sign in with your email and password
                </a>
                .
              </p>
            ) : (
              <>
                <p className="signin-instructions">
                  {availableMethods?.length
                    ? "This email is already registered. Sign in to continue:"
                    : "Choose how you’d like to sign in:"}
                </p>
                {errors.submit && (
                  <div className="error-message submit-error">
                    {errors.submit}
                  </div>
                )}
                <div className="signin-options">
                  {showSignInMethod("password") && (
                    <button
                      className="signin-button email"
                      onClick={handleEmailSetup}
                      disabled={isSigningIn}
                    >
                      {isSigningIn ? "Sending..." : "Set up email & password"}
                    </button>
                  )}
                  {showSignInMethod("google.com") && (
                    <button
                      className="signin-button google"
                      onClick={handleGoogleSignIn}
                      disabled={isSigningIn}
                    >
                      {isSigningIn ? "Signing in..." : "Sign in with Google"}
                    </button>
                  )}
                  {showSignInMethod("microsoft.com") && (
                    <button
                      className="signin-button microsoft"
                      onClick={handleMicrosoftSignIn}
                      disabled={isSigningIn}
                    >
                      {isSigningIn ? "Signing in..." : "Sign in with Microsoft"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentStepData = STEPS[currentStep];
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="demo-signup-container">
      <div className="demo-signup-content">
        <div className="demo-signup-header">
          <img
            className="demo-logo"
            alt="123 Wellness Logo"
            src="../images/123WellnessLogo green-02.png"
          />
          <h1>Start Your Wellness Journey</h1>
          <p className="demo-signup-subhead">
            Join thousands of educators transforming student wellbeing. Get
            started in seconds.
          </p>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <form onSubmit={handleNext} className="demo-signup-form">
          <div className="form-step">
            <label htmlFor={currentStepData.key} className="step-label">
              {currentStepData.label}
            </label>

            {currentStepData.type === "select" ? (
              <select
                ref={inputRef}
                id={currentStepData.key}
                name={currentStepData.key}
                value={formData[currentStepData.key]}
                onChange={handleChange}
                className={errors[currentStepData.key] ? "error" : ""}
              >
                {currentStepData.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                ref={inputRef}
                type={currentStepData.type}
                id={currentStepData.key}
                name={currentStepData.key}
                value={formData[currentStepData.key]}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className={errors[currentStepData.key] ? "error" : ""}
                placeholder={
                  currentStepData.placeholder ||
                  (currentStepData.type === "email"
                    ? "you@school.edu"
                    : "Type your answer...")
                }
              />
            )}

            {errors[currentStepData.key] && (
              <span className="error-message">
                {errors[currentStepData.key]}
              </span>
            )}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <div className="form-actions">
            {currentStep > 0 && (
              <button
                type="button"
                className="back-button"
                onClick={handleBack}
                aria-label="Go back to previous question"
              >
                ←
              </button>
            )}
            {!currentStepData.required && (
              <button
                type="button"
                className="skip-button"
                onClick={handleSkip}
                disabled={isSubmitting}
              >
                Skip
              </button>
            )}
            <button
              type="submit"
              className="demo-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Creating Account..."
                : currentStep === STEPS.length - 1
                  ? "Create Account"
                  : "Continue"}
            </button>
          </div>
        </form>

        <div className="demo-footer">
          <p>
            Already have an account?{" "}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              Sign in here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemoSignupScreen;
